import React, { useState } from "react";
import { BEER_TYPES } from "../data/beerTypes";
import { Beer, Thermometer, Flame, Utensils, Wheat, Droplets, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";

const METRICS = [
  { key: "amargor", icon: Flame },
  { key: "intensidad", icon: Droplets },
  { key: "cuerpo", icon: Beer },
  { key: "frescura", icon: Droplets },
  { key: "lupulo", icon: Wheat },
  { key: "alcohol", icon: Flame },
];

export default function BeerEducation() {
  const { t } = useTranslation();
  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10" data-testid="beer-education-page">
      <header className="mb-10 max-w-3xl">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">
          {t("beers.eyebrow")}
        </div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-[1]">
          {t("beers.title1")} <br />
          <span className="text-[#FEBA01]">{t("beers.title2")}</span>
        </h1>
        <p className="mt-5 text-base text-[#E5E5E5] leading-relaxed">
          {t("beers.desc")}
        </p>
      </header>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {BEER_TYPES.map((beer) => (
          <BeerCard key={beer.slug} beer={beer} t={t} />
        ))}
      </div>

      <section className="mt-16 bg-[#1A1A1A] border-2 border-[#333] p-6 lg:p-8">
        <h2 className="font-display text-3xl uppercase tracking-tight text-[#FEBA01]">
          {t("beers.ui.glossary")}
        </h2>
        <div className="mt-4 grid md:grid-cols-3 gap-4 text-sm text-[#E5E5E5]">
          {["abv", "srm", "ibu", "hops", "malt", "yeast"].map((k) => (
            <div key={k}>
              <div className="font-display text-lg text-white">{t(`beers.glossary.${k}.term`)}</div>
              <p className="text-[#A3A3A3]">{t(`beers.glossary.${k}.desc`)}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function BeerCard({ beer, t }) {
  const [open, setOpen] = useState(false);
  const tagline = t(`beers.data.${beer.slug}.tagline`, { defaultValue: beer.tagline });
  const description = t(`beers.data.${beer.slug}.description`, { defaultValue: beer.description });
  const colorLabel = t(`beers.data.${beer.slug}.colorLabel`, { defaultValue: beer.colorLabel });
  const grain = t(`beers.data.${beer.slug}.grain`, { defaultValue: beer.grain });
  const hops = t(`beers.data.${beer.slug}.hops`, { defaultValue: beer.hops });
  const pairing = t(`beers.data.${beer.slug}.pairing`, {
    returnObjects: true,
    defaultValue: beer.pairing,
  });
  const pairingArr = Array.isArray(pairing) ? pairing : beer.pairing;

  return (
    <div
      data-testid={`beer-card-${beer.slug}`}
      className="bg-[#1A1A1A] border-2 border-[#1A1A1A] hover:border-[#FEBA01] transition-colors overflow-hidden flex flex-col"
    >
      <div className="relative h-20 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(180deg, ${beer.color} 0%, ${beer.color}cc 55%, #0A0A0A 100%)` }}
        />
        <div className="absolute inset-0 bg-grain opacity-40" />
        <div className="relative p-4 flex items-start justify-between">
          <div>
            <h3 className="font-display text-3xl uppercase leading-none tracking-tight text-white drop-shadow-[2px_2px_0_rgba(0,0,0,0.85)]">
              {beer.name}
            </h3>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-[#FEBA01] drop-shadow-[1px_1px_0_rgba(0,0,0,0.9)]">
              {tagline}
            </div>
          </div>
          <div className="text-right">
            <div className="font-display text-2xl text-white leading-none drop-shadow-[1px_1px_0_rgba(0,0,0,0.85)]">{beer.abv}%</div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-[#FEBA01] drop-shadow-[1px_1px_0_rgba(0,0,0,0.9)]">ABV</div>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4 flex-1">
        <p className="text-sm text-[#E5E5E5] leading-relaxed">{description}</p>

        <div className="space-y-1.5">
          {METRICS.map((m) => (
            <RatingBar key={m.key} label={t(`beers.metrics.${m.key}`)} value={beer.ratings[m.key]} />
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-[#333]">
          <InfoRow icon={Thermometer} label={t("beers.ui.tempIdeal")} value={beer.tempC} />
          <InfoRow icon={Flame} label={t("beers.ui.kcal")} value={beer.calories} />
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid={`beer-toggle-${beer.slug}`}
          className="w-full flex items-center justify-center gap-1 text-[#FEBA01] font-mono text-[10px] uppercase tracking-widest border border-[#333] hover:border-[#FEBA01] py-2 transition-colors"
        >
          {open ? t("beers.ui.hideDetails") : t("beers.ui.seeDetails")}
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>

        {open && (
          <div className="space-y-3 border-t-2 border-[#FEBA01] pt-3 animate-in fade-in duration-200">
            <Detail label={t("beers.ui.color")} value={colorLabel} />
            <Detail label={t("beers.ui.grain")} value={grain} />
            <Detail label={t("beers.ui.hops")} value={hops} />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1 flex items-center gap-1">
                <Utensils size={10} /> {t("beers.ui.pairing")}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {pairingArr.map((p) => (
                  <span key={p} className="tag">{p}</span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RatingBar({ label, value }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3] shrink-0">
        {label}
      </div>
      <div className="flex-1 flex gap-0.5">
        {Array.from({ length: 10 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 h-2"
            style={{ backgroundColor: i < value ? "#FEBA01" : "#2A2A2A" }}
          />
        ))}
      </div>
      <div className="w-6 text-right font-mono text-[10px] text-[#FEBA01] shrink-0">{value}</div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={14} className="text-[#FEBA01] shrink-0" />
      <div className="min-w-0">
        <div className="font-mono text-[9px] uppercase tracking-widest text-[#A3A3A3] leading-none">{label}</div>
        <div className="font-display text-sm text-white truncate">{value}</div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3]">{label}</div>
      <div className="text-sm text-[#E5E5E5]">{value}</div>
    </div>
  );
}
